import React from "react";
import { createPortal } from "react-dom";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import usePopoverDismiss from "../../hooks/usePopoverDismiss.js";
import { getReactPortalRoot } from "../../utils/reactPortalRoot.js";

export const DispatchContextMenuContext = React.createContext(null);
const ITEMS = ':is([role="menuitem"], [role="menuitemcheckbox"], [role="menuitemradio"]):not(:disabled)';

// Stable portal containers keep preference requests and dialog state alive when
// the same controls move between their header menu and the combined menu.
export default function DispatchContextMenu({ children }) {
  const entries = React.useRef(new Map());
  const [position, setPosition] = React.useState(null);
  const previousFocus = React.useRef(null);
  const { refs, floatingStyles, isPositioned, update } = useFloating({
    open: Boolean(position), strategy: "fixed", placement: "bottom-start",
    middleware: [offset(2), flip({ padding: 8 }), shift({ padding: 8, crossAxis: true })],
    whileElementsMounted: autoUpdate,
  });
  const close = React.useCallback((restoreFocus = false) => {
    setPosition(null);
    if (restoreFocus && previousFocus.current?.isConnected) previousFocus.current.focus();
  }, []);
  const register = React.useCallback((key, entry) => {
    entries.current.set(key, entry);
    return () => { entries.current.delete(key); close(); };
  }, [close]);

  React.useEffect(() => {
    const open = (event) => {
      if (event.defaultPrevented || !event.target.closest?.('[data-dispatch-context-surface="true"]')
        || event.target.closest?.('dialog, [role="alertdialog"], [role="dialog"]:not([data-dispatch-context-surface="true"]), [role="menu"], [data-dispatch-truck-context="true"]')) return;
      if (!entries.current.size) return;
      event.preventDefault();
      previousFocus.current = document.activeElement;
      entries.current.forEach((entry) => entry.close());
      setPosition({ x: event.clientX, y: event.clientY });
    };
    document.addEventListener("contextmenu", open);
    return () => document.removeEventListener("contextmenu", open);
  }, []);

  React.useLayoutEffect(() => {
    if (!position) return;
    refs.setReference({ getBoundingClientRect: () => ({ x: position.x, y: position.y, top: position.y, bottom: position.y, left: position.x, right: position.x, width: 0, height: 0 }) });
    ["view", "more", "day"].forEach((key) => {
      const entry = entries.current.get(key);
      if (entry) refs.floating.current?.appendChild(entry.container);
    });
    update();
  }, [position, refs, update]);

  React.useEffect(() => {
    if (position && isPositioned) (refs.floating.current?.querySelector(ITEMS) || refs.floating.current)?.focus();
  }, [position, isPositioned, refs.floating]);
  usePopoverDismiss({
    floatingRef: refs.floating, isOpen: Boolean(position), onClose: () => close(),
    shouldIgnoreTarget: (target, event) => [...entries.current.values()].some((entry) => entry.shouldIgnoreTarget?.(target, event)),
  });
  const handleKeyDown = (event) => {
    const menu = refs.floating.current;
    if (!menu?.contains(event.target)) return;
    if (event.key === "Escape" || event.key === "Tab") {
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); }
      close(true);
    } else if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      const items = [...menu.querySelectorAll(ITEMS)];
      const index = items.indexOf(document.activeElement);
      const next = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1
        : (index + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
      items[next]?.focus();
    }
  };
  const handleClick = (event) => {
    if (!refs.floating.current?.contains(event.target)) return;
    const action = event.target.closest?.(ITEMS);
    if (action && !action.hasAttribute("data-keep-menu-open")) close(true);
  };
  const value = { register, close, isOpen: Boolean(position), handleKeyDown, handleClick };
  const portalRoot = getReactPortalRoot();
  return <DispatchContextMenuContext.Provider value={value}>
    {children}
    {portalRoot ? createPortal(<div ref={refs.setFloating} role="menu" aria-label="Actions du dispatch"
      tabIndex={-1} hidden={!position} className="dispatch-header-menu dispatch-header-more-menu dispatch-context-menu"
      onKeyDown={handleKeyDown}
      style={{ ...floatingStyles, visibility: position && !isPositioned ? "hidden" : undefined }} />, portalRoot) : null}
  </DispatchContextMenuContext.Provider>;
}
